import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewEncapsulation, inject, output, viewChild, input } from '@angular/core';
import {LiveAnnouncer} from '@angular/cdk/a11y';
import {Router} from '@angular/router';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {NavigationMenuService} from '@app/services/navigation-menu.service';
import {NavigationStateService} from '@app/services/navigation-state.service';
import {NavLayoutService} from '@app/services/nav-layout.service';
import {HighlightSegment, NavSearchEntry, NavSearchResult, highlightSegments, searchMenu} from '../model/nav-search';

let nextId = 0;

/**
 * Menu search: a combobox over both menus and the page bookmarks. Arrow keys move through
 * the results, Enter opens one (Ctrl/Cmd+Enter in a new tab), Shift+Enter stars it, Esc clears
 * and then leaves. Results are text, never HTML.
 */
@Component({
  selector: 'app-nav-menu-search',
  templateUrl: './nav-menu-search.component.html',
  styleUrls: ['./nav-menu-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class NavMenuSearchComponent implements OnInit, OnDestroy {
  private menu = inject(NavigationMenuService);
  private navigationState = inject(NavigationStateService);
  private navLayout = inject(NavLayoutService);
  private router = inject(Router);
  private announcer = inject(LiveAnnouncer);
  private cdr = inject(ChangeDetectorRef);

  readonly variant = input<'sidebar' | 'palette'>('sidebar');
  readonly limit = input(20);
  readonly queryChange = output<string>();
  /** A result was opened: the host closes itself and gives the page focus. */
  readonly picked = output<void>();
  /** Esc on an empty box. */
  readonly dismissed = output<void>();

  readonly input = viewChild<ElementRef<HTMLInputElement>>('input');

  readonly listId = `sn-search-results-${++nextId}`;
  readonly isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
  query = '';
  results: NavSearchResult[] = [];
  activeIndex = -1;

  private index: NavSearchEntry[] = [];
  private starred = new Set<string>();
  private readonly destroyed = new Subject<void>();

  ngOnInit(): void {
    this.menu.searchIndex$.pipe(takeUntil(this.destroyed)).subscribe((index) => {
      this.index = index;
      this.run();
    });
    this.menu.starredKeys$.pipe(takeUntil(this.destroyed)).subscribe((keys) => {
      this.starred = keys;
      this.cdr.markForCheck();
    });
    if (this.variant() === 'sidebar') {
      this.navLayout.focusSearch$.pipe(takeUntil(this.destroyed)).subscribe(() => this.focus());
    }
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  get shortcut(): string {
    return this.isMac ? '⌘K' : 'Ctrl K';
  }

  focus(): void {
    setTimeout(() => this.input()?.nativeElement.focus());
  }

  onInput(value: string): void {
    this.query = value;
    this.queryChange.emit(value);
    this.run();
    if (this.query.trim()) {
      this.announcer.announce(this.results.length
        ? `${this.results.length} ${this.results.length === 1 ? 'result' : 'results'}`
        : 'No menu items match', 'polite');
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!this.results.length) {
        return;
      }
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = (this.activeIndex + step + this.results.length) % this.results.length;
      document.getElementById(this.optionId(this.activeIndex))?.scrollIntoView({block: 'nearest'});
    } else if (event.key === 'Enter') {
      const result = this.results[this.activeIndex] || this.results[0];
      if (!result) {
        return;
      }
      event.preventDefault();
      if (event.shiftKey) {
        this.star(result.entry);
      } else {
        this.open(result.entry, event.ctrlKey || event.metaKey);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (this.query) {
        this.onInput('');
      } else {
        this.dismissed.emit();
        this.input().nativeElement.blur();
      }
    }
  }

  open(entry: NavSearchEntry, newTab = false): void {
    if (entry.external && entry.href) {
      window.open(entry.href, '_blank', 'noopener');
    } else if (entry.path) {
      if (newTab) {
        const url = this.router.serializeUrl(this.router.createUrlTree([entry.path], {queryParams: entry.queryParams || {}}));
        window.open(url, '_blank', 'noopener');
      } else {
        this.router.navigate([entry.path], {queryParams: entry.queryParams || {}});
      }
    }
    this.onInput('');
    this.picked.emit();
  }

  star(entry: NavSearchEntry): void {
    if (entry.node) {
      this.menu.toggleStar(entry.node);
    } else if (entry.bookmark) {
      this.navigationState.removeBookmark(entry.bookmark.key);
    }
    this.announcer.announce(this.isStarred(entry) ? `Removed ${entry.label} from favorites` : `Added ${entry.label} to favorites`);
  }

  isStarred(entry: NavSearchEntry): boolean {
    if (entry.bookmark) {
      return true;
    }
    const key = entry.node ? this.menu.starKey(entry.node) : null;
    return !!key && this.starred.has(key);
  }

  segments(result: NavSearchResult): HighlightSegment[] {
    return highlightSegments(result.entry.label, result.ranges);
  }

  optionId(index: number): string {
    return `${this.listId}-${index}`;
  }

  trackByKey(_index: number, result: NavSearchResult): string {
    return result.entry.key;
  }

  private run(): void {
    this.results = searchMenu(this.index, this.query, this.limit());
    this.activeIndex = this.results.length ? 0 : -1;
    this.cdr.markForCheck();
  }
}
